import React, { useCallback, useEffect, useRef, useState } from 'react';
import { IPublicClientApplication } from '@azure/msal-browser';
import BoardView from '../../../board/BoardView';
import ListView from '../../../list/ListView';
import GanttView from '../../../gantt/GanttView';
import CalendarView from '../../../calendar/CalendarView';
import BacklogView from '../../../components/agile/BacklogView';
import SprintView from '../../../components/agile/SprintView';
import { Project } from '../../../types';
import NewTaskModal from '../../../task/NewTaskModal';
import CreateCollectionModal from '../../../board/CreateCollectionModal';
import { getProjectCollections } from '../../../services/taskService';
import {
  BoardView as SavedBoardView, BoardViewSnapshot,
  listBoardViews, createBoardView, updateBoardView, deleteBoardView, setDefaultBoardView,
  deserialize,
} from '../../../services/boardViewService';
import { useCurrentUser } from '../../../hooks/useCurrentUser';
import { getLatestImportJobForProject } from '../../../services/importChecklistService';
import { ImportJob } from '../../../services/importService';
import ImportChecklistPanel, { importChecklistDismissKey } from '../../../components/ImportChecklistPanel';
import BoardModal, {
  BMCancelButton, BMPrimaryButton, BMDangerButton,
  BM_LABEL, BM_INPUT, BM_TITLE_INPUT,
} from '../../../components/BoardModal';

interface Props { pca: IPublicClientApplication; projectId: string; project?: Project; }

type ViewMode = 'board' | 'list' | 'gantt' | 'calendar' | 'backlog' | 'sprint';
export type BoardGroupBy = 'status' | 'assignee' | 'priority' | 'phase';

export default function TasksTab({ pca, projectId, project }: Props) {
  const agileOn = !!project?.dagrun_pm_module_agile;
  const me = useCurrentUser(pca, projectId);
  const [view, setView]             = useState<ViewMode>('board');
  const [showNew, setShowNew]       = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [groupBy, setGroupBy]       = useState<BoardGroupBy>('status');
  const [showNewCollection, setShowNewCollection] = useState(false);
  const [nextSortOrder, setNextSortOrder]         = useState<number>(0);

  // Post-import verification checklist — shown once (per import job) after an
  // import lands the user on the board, dismissable, and reopenable from the
  // Import tab.
  const [importJob, setImportJob] = useState<ImportJob | null>(null);
  useEffect(() => {
    let cancelled = false;
    getLatestImportJobForProject(pca, projectId)
      .then(j => {
        if (cancelled || !j) return;
        try { if (localStorage.getItem(importChecklistDismissKey(j.dagrun_pm_importjobid))) return; } catch { /* ignore */ }
        setImportJob(j);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [pca, projectId]);
  const dismissChecklist = () => {
    if (importJob) { try { localStorage.setItem(importChecklistDismissKey(importJob.dagrun_pm_importjobid), '1'); } catch { /* ignore */ } }
    setImportJob(null);
  };

  // Saved board views (personal).
  const [savedViews, setSavedViews] = useState<SavedBoardView[]>([]);
  const [activeViewId, setActiveViewId] = useState<string | null>(null);
  const [viewsDropdownOpen, setViewsDropdownOpen] = useState(false);
  const [showSaveView, setShowSaveView] = useState(false);
  const [showManageViews, setShowManageViews] = useState(false);
  const viewsWrapRef = useRef<HTMLDivElement>(null);
  const defaultApplied = useRef(false);

  const reloadViews = useCallback(async () => {
    if (!me) return;
    try {
      const vs = await listBoardViews(pca, projectId, me.oid);
      setSavedViews(vs);
      // On first load, auto-apply the default view (once) so the board
      // lands on the user's preferred state. Further navigation keeps
      // whatever state the user has picked.
      if (!defaultApplied.current) {
        const d = vs.find(v => v.dagrun_pm_isdefaultforuser);
        if (d) {
          const snap = deserialize(d);
          setGroupBy(snap.groupBy);
          setActiveViewId(d.dagrun_pm_boardviewid);
        }
        defaultApplied.current = true;
      }
    } catch { /* non-fatal */ }
  }, [pca, projectId, me]);
  useEffect(() => { reloadViews(); }, [reloadViews]);

  // Close the views dropdown on outside click.
  useEffect(() => {
    if (!viewsDropdownOpen) return;
    const onDoc = (e: MouseEvent) => {
      if (viewsWrapRef.current && !viewsWrapRef.current.contains(e.target as Node)) setViewsDropdownOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [viewsDropdownOpen]);

  const opt = (m: ViewMode, label: string) => {
    const isActive = view === m;
    return (
      <button
        key={m}
        onClick={() => setView(m)}
        style={{
          border: 'none',
          background: isActive ? 'var(--brand-mist)' : 'transparent',
          color: isActive ? 'var(--brand)' : 'var(--ink-3)',
          fontWeight: isActive ? 500 : 400,
          fontFamily: 'var(--font-ui)',
          fontSize: 12,
          padding: '4px 9px',
          borderRadius: 4,
          cursor: 'pointer',
          transition: 'background 0.15s, color 0.15s',
        }}
      >
        {label}
      </button>
    );
  };

  const openCreateCollection = async () => {
    try {
      const existing = await getProjectCollections(pca, projectId);
      const max = existing.reduce((m, c) => Math.max(m, c.dagrun_pm_sortorder ?? 0), 0);
      setNextSortOrder(max + 1);
    } catch {
      setNextSortOrder(0);
    }
    setShowNewCollection(true);
  };

  const applyView = (v: SavedBoardView) => {
    const snap: BoardViewSnapshot = deserialize(v);
    setGroupBy(snap.groupBy);
    setActiveViewId(v.dagrun_pm_boardviewid);
    setViewsDropdownOpen(false);
  };

  const activeView = activeViewId ? savedViews.find(v => v.dagrun_pm_boardviewid === activeViewId) : null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {importJob && (
        <div style={{ padding: '12px 24px 0' }}>
          <ImportChecklistPanel pca={pca} job={importJob} mode="panel" onClose={dismissChecklist} />
        </div>
      )}
      <div style={{
        padding: '12px 24px',
        display: 'flex', alignItems: 'center', gap: 12,
      }}>
        <div style={{
          display: 'inline-flex',
          gap: 2,
          padding: 2,
          background: 'var(--surface-2)',
          borderRadius: 'var(--r-sm)',
        }}>
          {opt('board', 'Board')}
          {opt('list', 'List')}
          {opt('gantt', 'Gantt')}
          {opt('calendar', 'Calendar')}
          {agileOn && opt('backlog', 'Backlog')}
          {agileOn && opt('sprint', 'Sprint')}
        </div>

        {/* Saved-views dropdown — only on Board view */}
        {view === 'board' && me && (
          <div ref={viewsWrapRef} style={{ position: 'relative' }}>
            <button
              type="button"
              onClick={() => setViewsDropdownOpen(o => !o)}
              style={{
                background: activeView ? '#D4EEF5' : 'transparent',
                border: '1px solid #0E7490',
                color: '#0E7490',
                fontFamily: 'var(--font-ui)',
                fontSize: 12, fontWeight: 600,
                padding: '5px 12px',
                borderRadius: 'var(--r-sm)',
                cursor: 'pointer',
                display: 'inline-flex', alignItems: 'center', gap: 6,
              }}
            >
              {activeView ? (
                <>
                  {activeView.dagrun_pm_isdefaultforuser && <span style={{ color: '#D97706' }}>★</span>}
                  Saved: {activeView.dagrun_pm_name}
                </>
              ) : (
                'Saved Views'
              )}
              <span style={{ fontSize: 9 }}>▾</span>
            </button>
            {viewsDropdownOpen && (
              <div style={{
                position: 'absolute', top: '100%', left: 0, marginTop: 4,
                background: '#fff', border: '1px solid #DDE0E6',
                borderRadius: 6, boxShadow: '0 4px 16px rgba(15,17,23,0.12)',
                minWidth: 220, zIndex: 60, padding: 4,
              }}>
                {savedViews.length === 0 && (
                  <div style={{ padding: '8px 12px', fontSize: 12, color: '#9198A8', fontStyle: 'italic' }}>
                    No saved views yet
                  </div>
                )}
                {savedViews.map(v => (
                  <button key={v.dagrun_pm_boardviewid}
                    type="button"
                    onClick={() => applyView(v)}
                    style={{
                      width: '100%', textAlign: 'left',
                      background: v.dagrun_pm_boardviewid === activeViewId ? '#F5F6F8' : 'transparent',
                      border: 'none', borderRadius: 4,
                      padding: '6px 10px',
                      fontSize: 13, color: '#0F1117',
                      cursor: 'pointer',
                      display: 'flex', alignItems: 'center', gap: 6,
                    }}
                    onMouseEnter={e => v.dagrun_pm_boardviewid !== activeViewId && (e.currentTarget.style.background = '#F5F6F8')}
                    onMouseLeave={e => v.dagrun_pm_boardviewid !== activeViewId && (e.currentTarget.style.background = 'transparent')}
                  >
                    {v.dagrun_pm_isdefaultforuser && <span style={{ color: '#D97706' }}>★</span>}
                    <span style={{ flex: 1 }}>{v.dagrun_pm_name}</span>
                  </button>
                ))}
                <div style={{ borderTop: '1px solid #EAECF0', marginTop: 4, paddingTop: 4 }}>
                  <button type="button"
                    onClick={() => { setShowSaveView(true); setViewsDropdownOpen(false); }}
                    style={{ width: '100%', textAlign: 'left', background: 'transparent', border: 'none', padding: '6px 10px', fontSize: 13, color: '#0E7490', cursor: 'pointer', fontWeight: 600 }}
                    onMouseEnter={e => (e.currentTarget.style.background = '#F5F6F8')}
                    onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                  >+ Save current view</button>
                  <button type="button"
                    onClick={() => { setShowManageViews(true); setViewsDropdownOpen(false); }}
                    style={{ width: '100%', textAlign: 'left', background: 'transparent', border: 'none', padding: '6px 10px', fontSize: 13, color: '#5A6272', cursor: 'pointer' }}
                    onMouseEnter={e => (e.currentTarget.style.background = '#F5F6F8')}
                    onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                  >Manage views…</button>
                </div>
              </div>
            )}
          </div>
        )}

        <div style={{ flex: 1 }} />
        {view === 'board' && (
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <span style={{ fontSize: 11, color: 'var(--ink-3)', fontFamily: 'var(--font-ui)' }}>Layout:</span>
            <select
              value={groupBy}
              onChange={e => { setGroupBy(e.target.value as BoardGroupBy); setActiveViewId(null); }}
              style={{
                fontFamily: 'var(--font-ui)', fontSize: 12,
                padding: '5px 8px',
                border: '1px solid var(--border)',
                borderRadius: 'var(--r-sm)',
                background: '#fff', color: 'var(--ink)',
                cursor: 'pointer',
                minWidth: 110,
              }}
            >
              <option value="status">Collections</option>
              <option value="assignee">Assignee</option>
              <option value="priority">Priority</option>
              <option value="phase">Phase</option>
            </select>
          </div>
        )}
        {view === 'board' && (
          <button
            type="button"
            onClick={openCreateCollection}
            style={{
              background: 'transparent',
              border: '1px solid #0E7490',
              color: '#0E7490',
              fontFamily: 'var(--font-ui)',
              fontSize: 12, fontWeight: 600,
              padding: '7px 14px',
              borderRadius: 'var(--r-sm)',
              cursor: 'pointer',
              transition: 'background 0.12s',
            }}
            onMouseEnter={e => (e.currentTarget.style.background = '#D4EEF5')}
            onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
          >
            + New Collection
          </button>
        )}
        <button className="btn-primary" onClick={() => setShowNew(true)}>+ New Task</button>
      </div>
      <div style={{ flex: 1, overflow: 'hidden' }}>
        {view === 'board'    && <BoardView    pca={pca} projectId={projectId} refreshKey={refreshKey} groupBy={groupBy} agile={agileOn} />}
        {view === 'list'     && <ListView     pca={pca} projectId={projectId} refreshKey={refreshKey} />}
        {view === 'gantt'    && <GanttView    pca={pca} projectId={projectId} refreshKey={refreshKey} />}
        {view === 'calendar' && <CalendarView pca={pca} projectId={projectId} refreshKey={refreshKey} />}
        {view === 'backlog'  && <BacklogView  pca={pca} projectId={projectId} refreshKey={refreshKey} />}
        {view === 'sprint'   && <SprintView   pca={pca} projectId={projectId} refreshKey={refreshKey} />}
      </div>

      {showNew && (
        <NewTaskModal
          pca={pca}
          projectId={projectId}
          agile={agileOn}
          onCreated={() => { setShowNew(false); setRefreshKey(k => k + 1); }}
          onCancel={() => setShowNew(false)}
        />
      )}

      {showNewCollection && (
        <CreateCollectionModal
          pca={pca}
          projectId={projectId}
          nextSortOrder={nextSortOrder}
          onCreated={() => { setShowNewCollection(false); setRefreshKey(k => k + 1); }}
          onCancel={() => setShowNewCollection(false)}
        />
      )}

      {showSaveView && me && (
        <SaveViewModal
          pca={pca}
          projectId={projectId}
          userId={me.oid}
          groupBy={groupBy}
          onClose={() => setShowSaveView(false)}
          onSaved={(id) => { setShowSaveView(false); setActiveViewId(id); reloadViews(); }}
        />
      )}

      {showManageViews && me && (
        <ManageViewsModal
          pca={pca}
          projectId={projectId}
          userId={me.oid}
          views={savedViews}
          activeViewId={activeViewId}
          onClose={() => setShowManageViews(false)}
          onChange={() => reloadViews()}
        />
      )}
    </div>
  );
}

// ── Save view modal ───────────────────────────────────────────────

function SaveViewModal({ pca, projectId, userId, groupBy, onClose, onSaved }: {
  pca: IPublicClientApplication;
  projectId: string;
  userId: string;
  groupBy: BoardGroupBy;
  onClose: () => void;
  onSaved: (id: string) => void;
}) {
  const [name, setName] = useState('');
  const [isDefault, setIsDefault] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    if (!name.trim() || saving) return;
    setSaving(true); setError(null);
    try {
      const id = await createBoardView(pca, {
        projectId,
        userId,
        name: name.trim(),
        groupBy,
        filters: {},  // current filter UI is in BoardView; no filter state to capture yet
      });
      if (isDefault) {
        try { await setDefaultBoardView(pca, id, projectId, userId); } catch { /* best effort */ }
      }
      onSaved(id);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed');
    } finally { setSaving(false); }
  };

  return (
    <BoardModal open title="Save board view" onClose={onClose}
      footer={<>
        <BMCancelButton onClick={onClose} disabled={saving} />
        <BMPrimaryButton onClick={submit} disabled={saving || !name.trim()}>
          {saving ? 'Saving…' : 'Save View'}
        </BMPrimaryButton>
      </>}>
      <div style={{ marginBottom: 14 }}>
        <label style={BM_LABEL}>View Name *</label>
        <input autoFocus value={name} onChange={e => setName(e.target.value)}
          style={BM_TITLE_INPUT} placeholder="e.g. My Open Work, Sprint Focus" />
      </div>
      <div style={{ marginBottom: 12, fontSize: 12, color: '#5A6272' }}>
        Captures: group-by (<strong>{groupBy}</strong>) and current filters.
        You can update this saved view later from the Views dropdown → Manage views.
      </div>
      <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#5A6272', cursor: 'pointer' }}>
        <input type="checkbox" checked={isDefault} onChange={e => setIsDefault(e.target.checked)} />
        Set as default — auto-load next time you open this board
      </label>
      {error && <div style={{ marginTop: 12, color: '#DC2626', fontSize: 12 }}>{error}</div>}
    </BoardModal>
  );
}

// ── Manage views modal ────────────────────────────────────────────

function ManageViewsModal({ pca, projectId, userId, views, activeViewId, onClose, onChange }: {
  pca: IPublicClientApplication;
  projectId: string;
  userId: string;
  views: SavedBoardView[];
  activeViewId: string | null;
  onClose: () => void;
  onChange: () => void;
}) {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState('');
  const [confirmDelete, setConfirmDelete] = useState<SavedBoardView | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const startRename = (v: SavedBoardView) => { setEditingId(v.dagrun_pm_boardviewid); setEditDraft(v.dagrun_pm_name); };
  const commitRename = async (v: SavedBoardView) => {
    const next = editDraft.trim();
    setEditingId(null);
    if (!next || next === v.dagrun_pm_name) return;
    try { await updateBoardView(pca, v.dagrun_pm_boardviewid, { dagrun_pm_name: next }); onChange(); } catch { /* ignore */ }
  };
  const toggleDefault = async (v: SavedBoardView) => {
    if (v.dagrun_pm_isdefaultforuser) {
      await updateBoardView(pca, v.dagrun_pm_boardviewid, { dagrun_pm_isdefaultforuser: false });
    } else {
      await setDefaultBoardView(pca, v.dagrun_pm_boardviewid, projectId, userId);
    }
    onChange();
  };
  const remove = (v: SavedBoardView) => { setConfirmDelete(v); setDeleteError(null); };

  const doDelete = async () => {
    if (!confirmDelete) return;
    setDeleting(true); setDeleteError(null);
    try {
      await deleteBoardView(pca, confirmDelete.dagrun_pm_boardviewid);
      // If the deleted view was active, signal back so caller can reset.
      if (activeViewId === confirmDelete.dagrun_pm_boardviewid) {
        // onChange triggers a reload that drops activeViewId implicitly via
        // the parent's defaultApplied gate. Best-effort.
      }
      setConfirmDelete(null);
      onChange();
    } catch (e) {
      setDeleteError(e instanceof Error ? e.message : 'Delete failed');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <BoardModal open title="Manage board views" onClose={onClose}
      footer={<BMCancelButton onClick={onClose}>Done</BMCancelButton>}>
      {views.length === 0 ? (
        <div style={{ padding: 16, textAlign: 'center', color: '#9198A8', fontSize: 13 }}>
          No saved views yet. Use "+ Save current view" from the Views dropdown to create one.
        </div>
      ) : (
        <div>
          {views.map(v => (
            <div key={v.dagrun_pm_boardviewid} style={{
              display: 'flex', alignItems: 'center', gap: 10,
              padding: '10px 0', borderBottom: '0.5px solid #EAECF0',
            }}>
              <button type="button" onClick={() => toggleDefault(v)}
                title={v.dagrun_pm_isdefaultforuser ? 'Unset default' : 'Set as default'}
                style={{ background: 'transparent', border: 'none', cursor: 'pointer', fontSize: 18, color: v.dagrun_pm_isdefaultforuser ? '#D97706' : '#C4CAD4' }}
              >★</button>
              {editingId === v.dagrun_pm_boardviewid ? (
                <input autoFocus
                  value={editDraft}
                  onChange={e => setEditDraft(e.target.value)}
                  onBlur={() => commitRename(v)}
                  onKeyDown={e => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); if (e.key === 'Escape') setEditingId(null); }}
                  style={{ ...BM_INPUT, flex: 1 }}
                />
              ) : (
                <span style={{ flex: 1, fontSize: 13, color: '#0F1117', cursor: 'pointer' }}
                  onClick={() => startRename(v)}
                >{v.dagrun_pm_name}</span>
              )}
              <BMDangerButton onClick={() => remove(v)}>Delete</BMDangerButton>
            </div>
          ))}
        </div>
      )}

      {confirmDelete && (
        <BoardModal open title="Delete view" onClose={() => !deleting && setConfirmDelete(null)}
          footer={<>
            <BMCancelButton onClick={() => setConfirmDelete(null)} disabled={deleting} />
            <BMDangerButton onClick={doDelete} disabled={deleting}>
              {deleting ? 'Deleting…' : 'Delete view'}
            </BMDangerButton>
          </>}>
          Delete this view? This cannot be undone.
          <div style={{ marginTop: 10 }}>
            <strong style={{ color: '#0F1117' }}>{confirmDelete.dagrun_pm_name}</strong>
          </div>
          {deleteError && <div style={{ marginTop: 10, color: '#DC2626', fontSize: 12 }}>{deleteError}</div>}
        </BoardModal>
      )}
    </BoardModal>
  );
}
